import { useState } from 'react'
import { Link } from 'react-router-dom'
import * as Popover from '@radix-ui/react-popover'
import {
  ChevronDown,
  Settings,
  UserPlus,
  Download,
  ArrowLeftRight,
  LogOut,
  Plus,
  Search,
  Pencil,
  Sun,
  Moon,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { authApi, workspaceApi } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { usePaletteStore } from '@/stores/palette-store'
import { useThemeStore } from '@/stores/theme-store'
import { cn } from '@/lib/utils'

interface WorkspaceMenuProps {
  currentSlug: string | null
  onNavigate?: () => void
}

function getInitials(name?: string | null, fallback = '?'): string {
  if (!name) return fallback
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function ShortcutBadge({ keys }: { keys: string[] }) {
  return (
    <span className="flex items-center gap-0.5 ml-auto shrink-0">
      {keys.map((k) => (
        <kbd
          key={k}
          className="rounded border border-sidebar-border bg-sidebar-accent/60 px-1 py-0.5 text-[10px] font-mono text-sidebar-foreground/50"
        >
          {k}
        </kbd>
      ))}
    </span>
  )
}

const menuItemClass = cn(
  'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors text-left',
  'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
)

export function WorkspaceMenu({ currentSlug, onNavigate }: WorkspaceMenuProps) {
  const [open, setOpen] = useState(false)

  const user = useAuthStore((s) => s.user)
  const clear = useAuthStore((s) => s.clear)
  const toggle = usePaletteStore((s) => s.toggle)
  const theme = useThemeStore((s) => s.theme)
  const setTheme = useThemeStore((s) => s.setTheme)

  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

  const { data: workspace } = useQuery({
    queryKey: ['workspace', currentSlug],
    queryFn: () => workspaceApi.getWorkspace(currentSlug!),
    enabled: !!currentSlug,
  })

  const workspaceInitials = workspace?.name
    ? workspace.name.slice(0, 2).toUpperCase()
    : 'OW'

  const userInitials = getInitials(user?.display_name ?? user?.email)
  const settingsHref = currentSlug
    ? `/workspaces/${currentSlug}/settings`
    : '/workspaces'

  async function handleSignOut() {
    await authApi.logout()
    clear()
  }

  function handleSearch() {
    setOpen(false)
    toggle()
  }

  function handleNavigate() {
    setOpen(false)
    onNavigate?.()
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className="flex flex-1 min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
          aria-label="Open workspace menu"
        >
          {/* Workspace icon */}
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground text-xs font-bold">
            {workspaceInitials}
          </div>

          {/* Workspace name */}
          <span className="flex-1 truncate text-sm font-semibold text-sidebar-foreground text-left">
            {workspace?.name ?? 'One Workspace'}
          </span>

          {/* User initials badge */}
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-[10px] font-semibold text-sidebar-accent-foreground">
            {userInitials}
          </div>

          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/50" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className={cn(
            'z-50 w-72 rounded-xl border border-sidebar-border bg-popover text-popover-foreground shadow-lg outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
          )}
        >
          {/* ── Section 1: User identity ── */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-sidebar-border">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt="avatar"
                className="h-9 w-9 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-sm font-semibold text-sidebar-accent-foreground">
                {userInitials}
              </div>
            )}
            <p className="flex-1 truncate text-sm font-medium">
              {user?.display_name ?? user?.email}
            </p>
            <button
              onClick={handleSearch}
              className="rounded-md p-1 hover:bg-sidebar-accent transition-colors shrink-0"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </button>
            <Popover.Close asChild>
              <Link
                to="/profile"
                onClick={onNavigate}
                className="rounded-md p-1 hover:bg-sidebar-accent transition-colors shrink-0"
                aria-label="Edit profile"
              >
                <Pencil className="h-4 w-4" />
              </Link>
            </Popover.Close>
          </div>

          {/* ── Section 2: Action items ── */}
          <div className="p-1.5 space-y-0.5">
            {/* Settings */}
            <Popover.Close asChild>
              <Link to={settingsHref} onClick={handleNavigate} className={menuItemClass}>
                <Settings className="h-4 w-4 shrink-0" />
                <span className="flex-1">Settings</span>
                <ShortcutBadge keys={['G', 'S']} />
              </Link>
            </Popover.Close>

            {/* Invite and manage members */}
            {currentSlug && (
              <Popover.Close asChild>
                <Link
                  to={`/workspaces/${currentSlug}/members`}
                  onClick={handleNavigate}
                  className={menuItemClass}
                >
                  <UserPlus className="h-4 w-4 shrink-0" />
                  <span className="flex-1">Invite and manage members</span>
                </Link>
              </Popover.Close>
            )}

            {/* Download desktop app — placeholder */}
            <button disabled className={cn(menuItemClass, 'opacity-40 cursor-default')}>
              <Download className="h-4 w-4 shrink-0" />
              <span className="flex-1">Download desktop app</span>
            </button>

            <div className="my-1 border-t border-sidebar-border" />

            {/* Switch workspace */}
            <Popover.Close asChild>
              <Link to="/workspaces" onClick={handleNavigate} className={menuItemClass}>
                <ArrowLeftRight className="h-4 w-4 shrink-0" />
                <span className="flex-1">Switch workspace</span>
                <ShortcutBadge keys={['O', 'W']} />
              </Link>
            </Popover.Close>

            <div className="my-1 border-t border-sidebar-border" />

            {/* Search */}
            <button onClick={handleSearch} className={menuItemClass}>
              <Search className="h-4 w-4 shrink-0" />
              <span className="flex-1">Cari...</span>
              <ShortcutBadge keys={['⌘', 'K']} />
            </button>

            {/* Theme toggle */}
            <button
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              className={menuItemClass}
            >
              {isDark ? (
                <Sun className="h-4 w-4 shrink-0" />
              ) : (
                <Moon className="h-4 w-4 shrink-0" />
              )}
              <span className="flex-1">{isDark ? 'Mode Terang' : 'Mode Gelap'}</span>
            </button>

            <div className="my-1 border-t border-sidebar-border" />

            {/* Log out */}
            <button onClick={handleSignOut} className={menuItemClass}>
              <LogOut className="h-4 w-4 shrink-0" />
              <span className="flex-1">Log out</span>
              <ShortcutBadge keys={['Alt', '⇧', 'Q']} />
            </button>
          </div>

          {/* ── Section 3: Add a workspace ── */}
          <div className="border-t border-sidebar-border p-1.5">
            <Popover.Close asChild>
              <Link
                to="/workspaces/new"
                onClick={handleNavigate}
                className={cn(menuItemClass, 'text-primary hover:text-primary')}
              >
                <Plus className="h-4 w-4 shrink-0" />
                <span>Add a workspace</span>
              </Link>
            </Popover.Close>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
